/* eslint-disable @typescript-eslint/indent */
/* eslint-disable no-confusing-arrow */
import React, { useContext, useEffect, useMemo, useState } from 'react';
import HotelApi, { HotelApiProduct } from '@customTypes/hotel-api';
import { store } from '@context/store';
import AVAILABILITY from '@constants/product';
import Currency from '@components/common/Currrency/Currency';
import showClosedForSeasonBanner from '@helpers/showClosedForSeasonBanner';
import useIsMobile from '@hooks/useIsMobile';
import useHotSpot from '@hooks/useHotSpot';
import { HotSpotMin } from '@customTypes/hot-spot';
import { FAIR_PRICING_SERVICE_FEE_RATE, FAIR_PRICING_VARIANT } from '@constants/fairPricing';

type Props = {
  hotel: HotelApi;
  minPadding?: boolean;
  isSeoPage?: boolean;
  showFairPricing: boolean;
};

export default function Products({
  hotel,
  minPadding = false,
  isSeoPage = false,
  showFairPricing,
}: Props) {
  const [isItemsCenter, setIsItemsCenter] = useState<boolean>(true);

  const globalState = useContext(store);
  const { state } = globalState;
  const { searchDate } = state;
  const isMobile = useIsMobile();

  const sortedProducts = useMemo(() => {
    const sortByProductType = (
      a: { product_type_sort_order: number },
      b: { product_type_sort_order: number },
    ) => a.product_type_sort_order - b.product_type_sort_order;

    const hotspotAvailable = hotel.products
      .filter((product) => product.is_hot_spot && product.availability === AVAILABILITY.AVAILABLE)
      .sort(sortByProductType);

    const hotspotUnavailable = hotel.products
      .filter((product) => product.is_hot_spot && product.availability !== AVAILABILITY.AVAILABLE)
      .sort(sortByProductType);

    const availableNonHotspot = hotel.products
      .filter((product) => !product.is_hot_spot && product.availability === AVAILABILITY.AVAILABLE)
      .sort(sortByProductType);

    const unavailableNonHotspot = hotel.products
      .filter((product) => !product.is_hot_spot && product.availability !== AVAILABILITY.AVAILABLE)
      .sort(sortByProductType);

    const products = [
      ...hotspotAvailable,
      ...hotspotUnavailable,
      ...availableNonHotspot,
      ...unavailableNonHotspot,
    ];

    return isMobile ? products : products.slice(0, 3);
  }, [hotel.products, isMobile]);

  const remainingProductsCount = hotel.products.length - sortedProducts.length;

  const isHotelUnavailableRelativeToToday = () => {
    if (!searchDate) {
      return !hotel.availability;
    }

    return false;
  };

  const getProductAvailabilityText = (product: HotelApiProduct) =>
    product.availability === AVAILABILITY.UNAVAILABLE
      ? AVAILABILITY.SOLD_OUT
      : AVAILABILITY.AVAILABLE;

  const { applyHotSpotDiscountToTotal } = useHotSpot();

  useEffect(() => {
    let itemsCenter = true;
    sortedProducts.forEach((product: HotelApiProduct) => {
      if (
        product.availability === AVAILABILITY.UNAVAILABLE ||
        (product.quantity && product.quantity > 0 && product.quantity < 5) ||
        (product.is_hot_spot && hotel.hotSpot && hotel.hotSpot.is_current_hot_spot_hotel)
      ) {
        itemsCenter = false;
      }
    });

    setIsItemsCenter(itemsCenter);
  }, [hotel, sortedProducts]);

  return (
    <ul className={`flex flex-row ${isItemsCenter ? 'items-center' : 'items-start'}`}>
      {sortedProducts &&
        sortedProducts.map((product: HotelApiProduct, index: number) => {
          const isProductUnavailable =
            isHotelUnavailableRelativeToToday() ||
            product.availability === AVAILABILITY.UNAVAILABLE ||
            (hotel.closedForSeason && showClosedForSeasonBanner(hotel.reopenDate, searchDate));

          const showAvailabilityWarning =
            product.quantity && product.quantity > 0 && product.quantity < 5;

          return (
            <React.Fragment key={product.id}>
              <li
                key={product.id}
                className={`box-content flex flex-col flex-shrink-0 items-start my-2 ${
                  minPadding ? 'px-3' : 'px-4'
                } ${index === 0 ? 'pl-6 d:pl-0' : ''} ${
                  index === sortedProducts.length - 1 ? 'pr-6 d:pr-4' : ''
                } border-r ${
                  index === sortedProducts.length - 1 && remainingProductsCount < 1
                    ? 'border-none'
                    : ''
                } d:min-w-78`}
              >
                <p
                  className={`uppercase text-13 font-rp-pn-semi-bold whitespace-nowrap mb-3px ${
                    isProductUnavailable ? 'text-rp-disabled  opacity-50' : 'text-custom-gray-3 '
                  } d:text-sm`}
                >
                  {product.name}
                </p>

                {(!searchDate || isSeoPage) && (
                  <p
                    className={`text-sm text-custom-gray-2 font-rp-pn-regular d:block ${
                      isProductUnavailable ? 'text-rp-disabled opacity-50' : 'text-custom-gray-2'
                    }`}
                  >
                    starting at
                  </p>
                )}

                <p
                  className={`text-22 font-rp-pn-semi-bold whitespace-nowrap ${
                    isProductUnavailable ? 'text-rp-disabled  opacity-50' : 'text-custom-black'
                  }`}
                >
                  {/* TODO: Refactor to use a single <Currency> component */}
                  {!showFairPricing && (
                    <div className="flex flex-col">
                      <Currency
                        value={
                          product.is_hot_spot &&
                          hotel.hotSpot &&
                          hotel.hotSpot.is_current_hot_spot_hotel
                            ? applyHotSpotDiscountToTotal(
                                +product.price,
                                hotel.hotSpot as HotSpotMin,
                                showFairPricing,
                              )
                            : product.price
                        }
                        valueClasses="text-21 font-rp-pn-semi-bold leading-6"
                      />

                      {product.is_hot_spot &&
                      hotel.hotSpot &&
                      hotel.hotSpot.is_current_hot_spot_hotel ? (
                        <Currency
                          value={product.price}
                          valueClasses="text-rp-disabled font-rp-pn-regular text-sm line-through leading-18px"
                          isoCodeClasses="hidden"
                        />
                      ) : null}
                    </div>
                  )}

                  {showFairPricing && FAIR_PRICING_VARIANT === 'variant1' && (
                    <div className="flex flex-col">
                      <Currency
                        value={
                          product.is_hot_spot &&
                          hotel.hotSpot &&
                          hotel.hotSpot.is_current_hot_spot_hotel
                            ? applyHotSpotDiscountToTotal(
                                +product.price,
                                hotel.hotSpot as HotSpotMin,
                                showFairPricing,
                              )
                            : product.price * (1 + FAIR_PRICING_SERVICE_FEE_RATE)
                        }
                        valueClasses="text-21 font-rp-pn-semi-bold leading-6"
                      />

                      {product.is_hot_spot &&
                      hotel.hotSpot &&
                      hotel.hotSpot.is_current_hot_spot_hotel ? (
                        <Currency
                          value={product.price * (1 + FAIR_PRICING_SERVICE_FEE_RATE)}
                          valueClasses="text-rp-disabled font-rp-pn-regular text-sm line-through leading-18px"
                          isoCodeClasses="hidden"
                        />
                      ) : null}

                      <p className="text-sm text-custom-gray-2 font-rp-pn-regular d:block">
                        Includes fees
                      </p>
                    </div>
                  )}

                  {showFairPricing && FAIR_PRICING_VARIANT === 'variant2' && (
                    <div className="flex flex-col">
                      <Currency
                        value={
                          product.is_hot_spot &&
                          hotel.hotSpot &&
                          hotel.hotSpot.is_current_hot_spot_hotel
                            ? applyHotSpotDiscountToTotal(
                                +product.price,
                                hotel.hotSpot as HotSpotMin,
                                showFairPricing,
                              )
                            : product.price
                        }
                        valueClasses="text-21 font-rp-pn-semi-bold leading-6"
                      />

                      {product.is_hot_spot &&
                      hotel.hotSpot &&
                      hotel.hotSpot.is_current_hot_spot_hotel ? (
                        <Currency
                          value={product.price}
                          valueClasses="text-rp-disabled font-rp-pn-regular text-sm line-through leading-18px"
                          isoCodeClasses="hidden"
                        />
                      ) : null}
                      <p className="text-sm text-custom-gray-2 font-rp-pn-regular d:block">
                        {`+$${(product.price * FAIR_PRICING_SERVICE_FEE_RATE).toFixed(0)} in fees`}
                      </p>
                    </div>
                  )}
                </p>

                {isProductUnavailable && (
                  <p className="uppercase text-xs mt-1 text-custom-black opacity-50 font-rp-pn-semi-bold">
                    {hotel.closedForSeason &&
                    showClosedForSeasonBanner(hotel.reopenDate, searchDate)
                      ? 'unavailable'
                      : getProductAvailabilityText(product)}
                  </p>
                )}

                {!isProductUnavailable && showAvailabilityWarning && searchDate && (
                  <p className="uppercase text-xs mt-1 text-rp-gold font-rp-pn-semi-bold">
                    {`ONLY ${product.quantity} LEFT`}
                  </p>
                )}
              </li>

              {index === sortedProducts.length - 1 && !isMobile && remainingProductsCount > 0 ? (
                <li key={999} className="my-2 h-full">
                  <p className="flex items-center min-h-53 text-sm font-rp-pn-semi-bold leading-normal px-4 tracking-0.6">
                    +{remainingProductsCount} MORE
                  </p>
                </li>
              ) : null}
            </React.Fragment>
          );
        })}
    </ul>
  );
}
